import { instance } from "api/apiBase";
import { CompactProperty } from "api/landlord/property-groups";
import {
  ApartmentsComVariant,
  ApplyBrandingCancelResponse,
  ApplyBrandingEnqueueResponse,
  ApplyBrandingResponse,
  ApplyCustomButtonLinkCancelResponse,
  ApplyCustomButtonLinkEnqueueResponse,
  ApplyCustomButtonLinkResponse,
  BrandingTargetsResponse,
  CustomButtonLinkScope,
  CustomButtonLinkTargetsResponse,
  Property,
  PropertyTemplateOption,
  TenantContact,
  Unit,
  UnitTemplateOptionListResponse,
  ZillowVariant
} from "types/landlord/address";
import {
  ApplicationNote,
  CreateNotePayload,
  EditNotePayload
} from "shared/app/Notes/types";
import { Application, PreviewApplicationRentByUnit } from "types/application";
import { MediaItem, Money, PropertyKind, Verification } from "shared/types";

export const QUERY_GET_PROPERTIES = "properties";
export const QUERY_SYNC_PROPERTY_STATUS = "sync_properties_status";
export const QUERY_SYNC_UNITS_STATUS = "sync_units_status";
export const QUERY_SYNC_RENTERS_STATUS = "sync_renters_status";

export const MUTATION_SYNC_PROPERTIES = "sync_properties";
export const MUTATION_SYNC_UNITS = "sync_units";
export const MUTATION_SYNC_RENTERS = "sync_renters";

export const MUTATION_SYNC_PROPERTY_BY_ID = "sync_property_by_id";
export const MUTATION_SYNC_UNIT_BY_ID = "sync_unit_by_id";
export const QUERY_SYNC_PROPERTY_BY_ID_STATUS = "sync_property_by_id_status";
export const QUERY_SYNC_UNIT_BY_ID_STATUS = "sync_unit_by_id_status";
export const QUERY_SYNC_PARTNER_STATUS = "sync_partner_status";

export const MUTATION_REVALIDATE_PROPERTY_ADDRESS =
  "revalidate_property_address";
export const MUTATION_REVALIDATE_PARTNER_ADDRESSES =
  "revalidate_partner_addresses";

export const QUERY_GET_APPFOLIO_PROPERTIES = "appfolio_properties";

const customButtonLinkBase = (scope: CustomButtonLinkScope, sourceId: number) =>
  `/portal/v1/${scope === "property" ? "properties" : "units"}/${sourceId}`;
export const MUTATION_ADD_PROPERTY_BY_ID = "add_property_by_id";
export const MUTATION_EXCLUDE_PROPERTY = "exclude_appfolio_property";
export const MUTATION_INCLUDE_PROPERTY = "include_appfolio_property";

const api = {
  getProperties: async ({
    page = 1,
    searchTerm = "",
    per_page = 12,
    entity_type,
    states,
    kinds,
    verification_template_identifiers,
    listing_template_identifiers
  }: {
    page?: number;
    searchTerm?: string;
    per_page?: number;
    entity_type?: "billing";
    states?: string[];
    kinds?: string[];
    verification_template_identifiers?: string[];
    listing_template_identifiers?: string[];
  } = {}) => {
    return (
      await instance.get(`/portal/v1/properties`, {
        params: {
          per_page,
          page,
          search: searchTerm,
          ...(entity_type ? { entity_type } : {}),
          ...(states?.length ? { "states[]": states } : {}),
          ...(kinds?.length ? { "kinds[]": kinds } : {}),
          ...(verification_template_identifiers?.length
            ? {
                "verification_template_identifiers[]":
                  verification_template_identifiers
              }
            : {}),
          ...(listing_template_identifiers?.length
            ? {
                "listing_template_identifiers[]": listing_template_identifiers
              }
            : {})
        }
      })
    ).data;
  },

  getBillingConfigurations: async ({
    page = 1,
    searchTerm = "",
    per_page = 12
  }: { page?: number; searchTerm?: string; per_page?: number } = {}) => {
    return (
      await instance.get(`/portal/v1/billing_configurations`, {
        params: {
          per_page,
          page,
          search: searchTerm
        }
      })
    ).data;
  },

  getPropertiesCompact: async (): Promise<{ items: CompactProperty[] }> => {
    return (await instance.get(`/portal/v1/properties/compact`)).data;
  },

  getProperty: async (propertyId: string) => {
    return (await instance.get(`/portal/v1/properties/${propertyId}`)).data;
  },

  revalidatePropertyAddress: async (propertyId: string | number) => {
    return (
      await instance.post<{ enqueued_count: number }>(
        `/portal/v1/properties/${propertyId}/revalidate_address`
      )
    ).data;
  },

  revalidatePartnerAddresses: async () => {
    return (await instance.post(`/portal/v1/properties/revalidate_addresses`))
      .data;
  },
  getPropertyTemplateOptions: async (propertyId: string | number) => {
    return (
      await instance.get<PropertyTemplateOption[]>(
        `/portal/v1/properties/${propertyId}/template_options`
      )
    ).data;
  },
  getPropertyMedia: async (propertyId: string | number) => {
    return (
      await instance.get<MediaItem[]>(
        `/portal/v1/properties/${propertyId}/media`
      )
    ).data;
  },
  getPropertyTenantContacts: async (propertyId: string | number) => {
    return (
      await instance.get<TenantContact[]>(
        `/portal/v1/properties/${propertyId}/tenant_contacts`
      )
    ).data;
  },
  getUnitsOfProperty: async ({
    propertyId,
    searchTerm = "",
    per_page = 12,
    page = 1,
    listed
  }: {
    propertyId: number;
    page?: number;
    searchTerm?: string;
    per_page?: number;
    listed?: boolean;
  }) => {
    return (
      await instance.get(`/portal/v1/properties/${propertyId}/units`, {
        params: {
          per_page,
          page,
          search: searchTerm,
          ...(listed === undefined ? {} : { listed })
        }
      })
    ).data;
  },
  getUnits: async ({
    page = 1,
    searchTerm = "",
    per_page = 12
  }: { page?: number; searchTerm?: string; per_page?: number } = {}) => {
    return (
      await instance.get(`/portal/v1/units`, {
        params: {
          per_page,
          page,
          search: searchTerm
        }
      })
    ).data;
  },
  // Listing pickers only need id/title/listed/rent/property, so they ask the API for the
  // `picker` view: a few columns per row instead of the units table's magic links, media,
  // lockboxes and activity stats. That's what makes a 1000-row page affordable.
  getPickerUnits: async ({
    page = 1,
    per_page = 1000,
    searchTerm = ""
  }: { page?: number; per_page?: number; searchTerm?: string } = {}) => {
    return (
      await instance.get(`/portal/v1/units`, {
        params: { view: "picker", per_page, page, search: searchTerm }
      })
    ).data;
  },
  getAllUnits: async () => {
    return (await instance.get(`/portal/v1/units`)).data;
  },
  getUnitById: async (unitId: string): Promise<Unit> => {
    return (await instance.get<Unit>(`/portal/v1/units/${unitId}`)).data;
  },
  getUnitByPropertyAndUnitId: async (propertyId: string, unitId: string) => {
    return (
      await instance.get<Unit>(
        `/portal/v1/properties/${propertyId}/units/${unitId}`
      )
    ).data;
  },
  getUnitTemplateOptions: async (
    unitId: string
  ): Promise<UnitTemplateOptionListResponse> => {
    return (
      await instance.get<UnitTemplateOptionListResponse>(
        `/portal/v1/units/${unitId}/template_options`
      )
    ).data;
  },
  editUnit: async (
    unitId: number,
    settings: {
      title?: string;
      monthly_rent_amount?: Money;
      home_rent_amount?: Money;
      site_rent_amount?: Money;
      available_at?: string;
      ready_for_showing_at?: string;
      occupied?: boolean;
      waitlist?: boolean;
      plan?: {
        sqft?: number;
        beds?: number;
        baths?: number;
      };
      external_id?: string;
      description?: string;
      media_session_key?: string;
      media?: {
        content_type: string;
        resolutions: {
          asset_id: string;
          resolution: string;
        }[];
      }[];
      pms_monthly_rent_amount_override?: boolean;
      pms_home_rent_amount_override?: boolean;
      pms_site_rent_amount_override?: boolean;
      pms_occupied_override?: boolean;
      pms_available_at_override?: boolean;
      pms_ready_for_showing_at_override?: boolean;
      pms_overrides?: {
        sale_price?: boolean;
        purchase_price?: boolean;
        cats_allowed?: boolean;
        dogs_allowed?: boolean;
        dimensions?: boolean;
        sqft?: boolean;
        beds?: boolean;
        baths?: boolean;
        amenities?: boolean;
        lease_terms?: boolean;
        included_utilities?: boolean;
        title?: boolean;
        description?: boolean;
        address?: boolean;
        custom_button_link?: boolean;
      };
      address?: {
        address1?: string;
        address2?: string;
        city?: string;
        state_or_province?: string;
        postal_code?: string;
      };
      pms_monthly_rent_amount?: number;
      sale_price?: Money;
      purchase_price?: Money;
      // AppFolio-sourced fees in integer cents. Sending either of these marks
      // the corresponding field overridden server-side so subsequent AppFolio
      // syncs won't clobber the manual value.
      application_fee_cents?: number | null;
      security_deposit_cents?: number | null;
      application_fee_overridden?: boolean;
      security_deposit_overridden?: boolean;
      dimensions?: string;
      lease_terms?: string[];
      included_utilities?: string[];
      custom_button_link?: string | null;
    }
  ): Promise<Unit> => {
    return await instance.patch(`/portal/v1/units/${unitId}`, { ...settings });
  },
  deleteUnit: async (unitId: number) => {
    return (await instance.delete(`/portal/v1/units/${unitId}`)).data;
  },
  assignUnitLockbox: async (
    unitId: number,
    lockboxId: number | null
  ): Promise<Unit> => {
    return (
      await instance.patch(`/portal/v1/units/${unitId}/lockbox`, {
        lockbox_id: lockboxId
      })
    ).data;
  },
  updateUnitListingOverrides: async (
    unitId: number,
    overrides: { listed?: boolean; available_for_application?: boolean }
  ): Promise<Unit> => {
    return (
      await instance.patch(
        `/portal/v1/units/${unitId}/listing_overrides`,
        overrides
      )
    ).data;
  },
  updateMagicLink: async (
    linkId: number,
    payload: {
      active?: boolean;
      listed?: boolean;
      feed_zillow_enabled?: boolean;
      feed_zillow_spotlight_enabled?: boolean;
      feed_zillow_variant?: ZillowVariant | null;
      feed_apartments_com_enabled?: boolean;
      feed_apartments_com_variant?: ApartmentsComVariant | null;
      feed_zumper_enabled?: boolean;
      feed_dwellsy_enabled?: boolean;
      feed_rental_beast_enabled?: boolean;
    }
  ) => {
    return (await instance.patch(`/portal/v1/magic_links/${linkId}`, payload))
      .data;
  },
  getAllAplications: async ({
    page = 1,
    searchTerm = "",
    per_page = 10
  }: { page?: number; searchTerm?: string; per_page?: number } = {}) => {
    return (
      await instance.get(`/portal/v1/applications`, {
        params: {
          per_page,
          page,
          search: searchTerm
        }
      })
    ).data;
  },
  getApplicationById: async (id: string) => {
    return (await instance.get<Application>(`/portal/v1/applications/${id}`))
      .data;
  },
  approveApplication: async (
    applicationId: string,
    details: {
      conditions?: string[];
      note_message?: string;
      email_message?: string;
      skip_sending_email?: boolean;
      conditional_approval?: boolean;
      send_applicants_to_pms?: boolean;
    }
  ): Promise<Application> => {
    return (
      await instance.post(`/portal/v1/applications/${applicationId}/approve`, {
        ...details
      })
    ).data;
  },
  rejectApplication: async (
    applicationId: string,
    details: {
      reasons: string[];
      note_message?: string;
      email_message?: string;
      skip_sending_email?: boolean;
    }
  ): Promise<Application> => {
    return (
      await instance.post(`/portal/v1/applications/${applicationId}/reject`, {
        ...details
      })
    ).data;
  },

  checkApprovedAppWarning: async (
    applicationId: string
  ): Promise<{
    warning: boolean;
    approved_applications: { id: string; approved_at: string }[];
  }> => {
    return (
      await instance.get(
        `/portal/v1/applications/${applicationId}/check_approved_app_warning`
      )
    ).data;
  },

  addApplicationNote: async (
    applicationId: string,
    data: CreateNotePayload
  ): Promise<ApplicationNote> => {
    return (
      await instance.post(
        `/portal/v1/boom_screen_applications/${applicationId}/notes`,
        data
      )
    ).data;
  },
  editApplicationNote: async (
    applicationId: string,
    noteId: number,
    data: EditNotePayload
  ): Promise<ApplicationNote> => {
    return (
      await instance.patch(
        `/portal/v1/boom_screen_applications/${applicationId}/notes/${noteId}`,
        data
      )
    ).data;
  },
  deleteApplicationNote: async (applicationId: string, noteId: number) => {
    return (
      await instance.delete(
        `/portal/v1/boom_screen_applications/${applicationId}/notes/${noteId}`
      )
    ).data;
  },
  deleteNoteAttachment: async (applicationId: string, attachmentId: number) => {
    return (
      await instance.delete(
        `/portal/v1/applications/${applicationId}/notes/attachments/${attachmentId}`
      )
    ).data;
  },
  updateAllLinks: async (property_id: string, active: boolean) => {
    return (
      await instance.patch(`/portal/v1/magic_links/batch_update_by_property`, {
        property_id,
        active
      })
    ).data;
  },
  inviteApplicantByUnitId: async (email: string, unit_id: string) => {
    return (
      await instance.post("/portal/v1/magic_links", {
        email,
        unit_id
      })
    ).data;
  },
  inviteApplicantByPropertyId: async (email: string, property_id: string) => {
    return (
      await instance.post("/portal/v1/magic_links", {
        email,
        property_id
      })
    ).data;
  },
  batchInviteApplicants: async (invites: {
    unit_id?: number;
    application_id?: number;
    invitees: {
      email: string;
      role: string;
    }[];
  }) => {
    return (
      await instance.post("/portal/v1/magic_links/batch", {
        ...invites
      })
    ).data;
  },
  addUnits: async (
    propertyId: string,
    unit: {
      title: string;
      monthly_rent_amount?: Money;
      home_rent_amount?: Money;
      site_rent_amount?: Money;
      available_at?: string;
      occupied?: boolean;
    }
  ) => {
    return (
      await instance.post(`/portal/v1/properties/${propertyId}/units`, {
        ...unit
      })
    ).data;
  },
  batchAddUnits: async (units: Unit[], propertyId: string) => {
    return await instance.post(
      `/portal/v1/properties/${propertyId}/units/batch`,
      { units }
    );
  },
  addProperty: async ({
    use_property_group_verification_template,
    name,
    owner_details,
    address,
    units,
    external_id,
    verification_template_id
  }: {
    use_property_group_verification_template: boolean;
    name: string;
    owner_details?: {
      property_owner_name?: string;
      property_owner_email?: string;
      property_owner_phone?: string;
    };
    address: {
      address1: string;
      address2?: string;
      postal_code: string;
      city: string;
      country: string;
      state_or_province: string;
    };
    units: Unit[];
    external_id?: string;
    verification_template_id?: string;
  }): Promise<Property> => {
    return await instance
      .post("/portal/v1/properties", {
        use_property_group_verification_template,
        name,
        external_id,
        address,
        units,
        verification_template_id,
        owner_details
      })
      .then((p) => p.data);
  },
  getPropertyRenters: async (propertyId: string) => {
    return (await instance.get(`/portal/v1/properties/${propertyId}/renters`))
      .data;
  },
  getBrandingTargets: async ({
    propertyId,
    page,
    per_page,
    search
  }: {
    propertyId: number;
    page: number;
    per_page: number;
    search?: string;
  }): Promise<BrandingTargetsResponse> => {
    return (
      await instance.get(
        `/portal/v1/properties/${propertyId}/branding_targets`,
        { params: { page, per_page, ...(search ? { search } : {}) } }
      )
    ).data;
  },
  applyBranding: async (
    params:
      | { propertyId: number; target_property_ids: number[] }
      | {
          propertyId: number;
          apply_to_all: true;
          search?: string;
          excluded_property_ids?: number[];
        }
  ): Promise<ApplyBrandingEnqueueResponse> => {
    const { propertyId, ...body } = params;

    return (
      await instance.post(
        `/portal/v1/properties/${propertyId}/apply_branding`,
        body
      )
    ).data;
  },
  getApplyBrandingStatus: async ({
    propertyId,
    bulkApplyId
  }: {
    propertyId: number;
    bulkApplyId: number;
  }): Promise<ApplyBrandingResponse> => {
    return (
      await instance.get(
        `/portal/v1/properties/${propertyId}/apply_branding/${bulkApplyId}`
      )
    ).data;
  },
  cancelApplyBranding: async ({
    propertyId,
    bulkApplyId
  }: {
    propertyId: number;
    bulkApplyId: number;
  }): Promise<ApplyBrandingCancelResponse> => {
    return (
      await instance.post(
        `/portal/v1/properties/${propertyId}/apply_branding/${bulkApplyId}/cancel`
      )
    ).data;
  },
  getCustomButtonLinkTargets: async ({
    scope,
    sourceId,
    page,
    per_page,
    search
  }: {
    scope: CustomButtonLinkScope;
    sourceId: number;
    page: number;
    per_page: number;
    search?: string;
  }): Promise<CustomButtonLinkTargetsResponse> => {
    return (
      await instance.get(
        `${customButtonLinkBase(scope, sourceId)}/custom_button_link_targets`,
        { params: { page, per_page, ...(search ? { search } : {}) } }
      )
    ).data;
  },
  applyCustomButtonLink: async (
    params: { scope: CustomButtonLinkScope; sourceId: number } & (
      | { target_ids: number[] }
      | { apply_to_all: true; search?: string; excluded_ids?: number[] }
    )
  ): Promise<ApplyCustomButtonLinkEnqueueResponse> => {
    const { scope, sourceId, ...body } = params;

    return (
      await instance.post(
        `${customButtonLinkBase(scope, sourceId)}/apply_custom_button_link`,
        body
      )
    ).data;
  },
  getApplyCustomButtonLinkStatus: async ({
    scope,
    sourceId,
    bulkApplyId
  }: {
    scope: CustomButtonLinkScope;
    sourceId: number;
    bulkApplyId: number;
  }): Promise<ApplyCustomButtonLinkResponse> => {
    return (
      await instance.get(
        `${customButtonLinkBase(scope, sourceId)}/apply_custom_button_link/${bulkApplyId}`
      )
    ).data;
  },
  cancelApplyCustomButtonLink: async ({
    scope,
    sourceId,
    bulkApplyId
  }: {
    scope: CustomButtonLinkScope;
    sourceId: number;
    bulkApplyId: number;
  }): Promise<ApplyCustomButtonLinkCancelResponse> => {
    return (
      await instance.post(
        `${customButtonLinkBase(scope, sourceId)}/apply_custom_button_link/${bulkApplyId}/cancel`
      )
    ).data;
  },
  editProperty: async (data: {
    propertyId: number;
    name?: string;
    kind?: PropertyKind | null;
    address?: {
      address1: string;
      address2?: string;
      postal_code?: string;
      city?: string;
      country?: string;
      state_or_province?: string;
    };
    description?: string;
    external_id?: string;
    verification_template_id?: string;
    prescreening_template_identifier?: string;
    use_property_group_verification_template?: boolean;
    amenity_ids?: number[];
    pet_policy?: {
      cats_allowed?: boolean;
      dogs_allowed?: boolean;
    };
    pms_overrides?: {
      amenities?: boolean;
      cats_allowed?: boolean;
      dogs_allowed?: boolean;
      description?: boolean;
      property_owner_name?: boolean;
      property_owner_phone?: boolean;
      property_owner_email?: boolean;
      landlord_name?: boolean;
      landlord_phone?: boolean;
      landlord_email?: boolean;
      custom_button_link?: boolean;
      name?: boolean;
      address?: boolean;
      market?: boolean;
    };
    owner_details?: {
      property_owner_name?: string;
      property_owner_phone?: string;
      property_owner_email?: string;
    };
    media?: {
      content_type: string;
      resolutions: {
        asset_id: string;
        resolution: string;
      }[];
    }[];
    media_session_key?: string;
    branding?: {
      enabled?: boolean;
      logo?: string;
      primary_color?: string;
      landlord_name?: string;
      landlord_phone?: string;
      landlord_email?: string;
      palette?: Record<string, string>;
    };
    template_options?: {
      id?: number;
      name: string;
      verification_template_identifier: string;
    }[];
    custom_button_link?: string | null;
    property_shield_monitoring_enabled?: boolean;
  }) => {
    return (
      await instance.patch(`/portal/v1/properties/${data.propertyId}`, data)
    ).data;
  },
  deleteProperty: async (propertyId: string) => {
    return (await instance.delete(`/portal/v1/properties/${propertyId}`)).data;
  },
  updatePropertyBilling: async (
    propertyId: number,
    billing: {
      bank_account_id?: number | null;
      billing_configuration_id?: number | null;
    }
  ) => {
    return (
      await instance.patch(
        `/portal/v1/properties/${propertyId}/billing`,
        billing
      )
    ).data;
  },
  requestPropertyManagementSystemSync: async (
    resource: string,
    integrationCredentialId?: number
  ) => {
    return (
      await instance.post(
        `/portal/v1/property_management_system/${resource}/sync`,
        integrationCredentialId
          ? { integration_credential_id: integrationCredentialId }
          : undefined
      )
    ).data;
  },
  getPropertyManagementSystemSyncStatus: async (resource: string) => {
    return (
      await instance.get(
        `/portal/v1/property_management_system/${resource}/sync_status`
      )
    ).data;
  },
  requestPropertyManagementSystemSyncById: async (
    resource: string,
    id: string
  ) => {
    return (
      await instance.post(
        `/portal/v1/property_management_system/${resource}/${id}/sync`
      )
    ).data;
  },
  getPropertyManagementSystemSyncByIdStatus: async (
    resource: string,
    id: string
  ) => {
    return (
      await instance.get(
        `/portal/v1/property_management_system/${resource}/${id}/sync_status`
      )
    ).data;
  },
  updateApplicationUnit: async (
    application_id: string,
    unit_id: number,
    rent_data: {
      change_type: "use_current" | "use_listed" | "manual";
      monthly_rent_amount?: Money;
      home_rent_amount?: Money;
      site_rent_amount?: Money;
    },
    template_options?: {
      keep_existing_template?: boolean;
      verification_template_id?: string;
    }
  ): Promise<Application> => {
    return (
      await instance.patch<Application>(
        `/portal/v1/applications/${application_id}/unit`,
        {
          unit_id,
          rent_data,
          ...template_options
        }
      )
    ).data;
  },
  getManualAnimalScreeningReport: async (
    verification_id: number
  ): Promise<Verification> => {
    return (
      await instance.post(`/portal/v1/pet_screenings/sync`, {
        verification_id
      })
    ).data;
  },
  deleteMediaItem: async (mediaId: string) => {
    return (
      await instance.delete(`/portal/v1/listings/media/${mediaId}/delete`)
    ).data;
  },

  getAppfolioProperties: async ({
    page = 1,
    limit = 100,
    sortBy,
    sortOrder,
    search,
    statusFilter
  }: {
    page?: number;
    limit?: number;
    sortBy?: string;
    sortOrder?: string;
    search?: string;
    statusFilter?: string;
  }) => {
    return (
      await instance.get(`/portal/v1/properties/appfolio/browse`, {
        params: {
          page,
          limit,
          ...(sortBy && { sortBy }),
          ...(sortOrder && { sortOrder }),
          ...(search && { search }),
          ...(statusFilter && { status_filter: statusFilter })
        }
      })
    ).data;
  },

  addPropertyById: async (propertyId: string) => {
    return (
      await instance.post(`/portal/v1/properties/appfolio/add`, {
        property_id: propertyId
      })
    ).data;
  },
  getPreviewApplicationRentByUnit: async (
    application_id: string,
    unit_id: number,
    verification_template_id?: string
  ): Promise<PreviewApplicationRentByUnit> => {
    return (
      await instance.get(
        `/portal/v1/applications/${application_id}/preview_application_rent_by_unit`,
        {
          params: {
            unit_id: unit_id.toString(),
            ...(verification_template_id ? { verification_template_id } : {})
          }
        }
      )
    ).data;
  },

  excludeProperty: async (propertyId: string) => {
    return (
      await instance.post(`/portal/v1/properties/appfolio/exclude`, {
        property_id: propertyId
      })
    ).data;
  },

  includeProperty: async (propertyId: string) => {
    return (
      await instance.post(`/portal/v1/properties/appfolio/include`, {
        property_id: propertyId
      })
    ).data;
  }
};

export default api;
